import React, { useMemo, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
import { Search, Filter, Plus, Edit2, Trash2, Eye, Calendar, Globe, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { AstroEvent, AstroEventType, CmsStatus, SupportedLang, EVENT_TYPE_LABELS, STATUS_LABELS, formatDateRu, formatDateTimeFull, createEmptyEvent } from '../../data/forecastCmsData';
interface AdminAstroEventsManagerProps {
  events: AstroEvent[];
  setEvents: React.Dispatch<React.SetStateAction<AstroEvent[]>>;
  setCreatePanel: (panel: any) => void;
  success: (msg: string) => void;
  renderTableHeader: (title: string, count: number, onExport?: () => void, onCreate?: () => void, createLabel?: string) => React.ReactNode;
}
export function AdminAstroEventsManager({
  events,
  setEvents,
  setCreatePanel,
  success,
  renderTableHeader
}: AdminAstroEventsManagerProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<AstroEventType | 'all'>('all');
  const [filterLang, setFilterLang] = useState<SupportedLang | 'all'>('all');
  const [editingEvent, setEditingEvent] = useState<AstroEvent | null>(null);
  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // Tab filter
      if (activeTab !== 'all' && event.status !== activeTab) return false;
      // Search filter
      if (searchQuery && !event.title.toLowerCase().includes(searchQuery.toLowerCase()) && !event.slug.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Type filter
      if (filterType !== 'all' && event.event_type !== filterType) return false;
      // Lang filter
      if (filterLang !== 'all' && event.lang !== filterLang) return false;
      return true;
    }).sort((a, b) => new Date(b.date_start).getTime() - new Date(a.date_start).getTime());
  }, [events, activeTab, searchQuery, filterType, filterLang]);
  const tabs = [{
    id: 'all',
    label: 'Все события'
  }, {
    id: 'published',
    label: 'Опубликованные'
  }, {
    id: 'scheduled',
    label: 'Запланированные'
  }, {
    id: 'draft',
    label: 'Черновики'
  }, {
    id: 'archived',
    label: 'Архив'
  }];
  const handleStatusChange = (id: string, newStatus: CmsStatus) => {
    setEvents(events.map(e => e.id === id ? {
      ...e,
      status: newStatus,
      updated_at: new Date().toISOString()
    } : e));
    success(`Статус изменен на "${STATUS_LABELS[newStatus].ru}"`);
  };
  const handleDelete = (id: string) => {
    if (confirm('Вы уверены, что хотите удалить это событие?')) {
      setEvents(events.filter(e => e.id !== id));
      success('Событие удалено');
    }
  };
  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;
    if (events.some(ev => ev.id === editingEvent.id)) {
      // Update
      setEvents(events.map(ev => ev.id === editingEvent.id ? {
        ...editingEvent,
        updated_at: new Date().toISOString()
      } : ev));
      success('Событие обновлено');
    } else {
      // Create
      setEvents([editingEvent, ...events]);
      success('Событие создано');
    }
    setEditingEvent(null);
  };
  const handleCreateNew = () => {
    setEditingEvent(createEmptyEvent());
  };
  if (editingEvent) {
    return <div className="space-y-6" data-id="element-1792">
        <div className="flex items-center justify-between" data-id="element-1793">
          <div data-id="element-1794">
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-1795">
              {events.some(e => e.id === editingEvent.id) ? 'Редактирование события' : 'Новое событие'}
            </h2>
            <p className="text-slate-500" data-id="element-1796">Заполните все необходимые поля</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-1797">
            <Button variant="outline" onClick={() => setEditingEvent(null)} data-id="element-1798">
              Отмена
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} data-id="element-1799">
              Сохранить
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1800">
          <div className="lg:col-span-2 space-y-6" data-id="element-1801">
            <Card padding="lg" className="space-y-6" data-id="element-1802">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-1803">
                Основная информация
              </h3>

              <div className="space-y-4" data-id="element-1804">
                <div data-id="element-1805">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1806">
                    Название события *
                  </label>
                  <Input value={editingEvent.title} onChange={e => setEditingEvent({
                  ...editingEvent,
                  title: e.target.value
                })} placeholder="Например: Солнечное затмение в Овне" data-id="element-1807" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1808">
                  <div data-id="element-1809">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1810">
                      Тип события *
                    </label>
                    <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingEvent.event_type} onChange={e => setEditingEvent({
                    ...editingEvent,
                    event_type: e.target.value as AstroEventType
                  })} data-id="element-1811">
                      {Object.entries(EVENT_TYPE_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-1812">
                          {val.ru}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-1813">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1814">
                      Язык *
                    </label>
                    <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingEvent.lang} onChange={e => setEditingEvent({
                    ...editingEvent,
                    lang: e.target.value as SupportedLang
                  })} data-id="element-1815">
                      <option value="ru" data-id="element-1816">Русский</option>
                      <option value="en" data-id="element-1817">English</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1818">
                  <div data-id="element-1819">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1820">
                      Дата начала *
                    </label>
                    <Input type="date" value={editingEvent.date_start} onChange={e => setEditingEvent({
                    ...editingEvent,
                    date_start: e.target.value
                  })} data-id="element-1821" />
                  </div>
                  <div data-id="element-1822">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1823">
                      Дата окончания
                    </label>
                    <Input type="date" value={editingEvent.date_end || ''} onChange={e => setEditingEvent({
                    ...editingEvent,
                    date_end: e.target.value || null
                  })} data-id="element-1824" />
                  </div>
                  <div data-id="element-1825">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1826">
                      Точное время
                    </label>
                    <Input type="time" value={editingEvent.time_exact || ''} onChange={e => setEditingEvent({
                    ...editingEvent,
                    time_exact: e.target.value || null
                  })} data-id="element-1827" />
                  </div>
                </div>
              </div>
            </Card>

            <Card padding="lg" className="space-y-6" data-id="element-1828">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-1829">
                Контент
              </h3>

              <div className="space-y-4" data-id="element-1830">
                <div data-id="element-1831">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1832">
                    Краткое описание (для карточек)
                  </label>
                  <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingEvent.description_short} onChange={e => setEditingEvent({
                  ...editingEvent,
                  description_short: e.target.value
                })} placeholder="Краткая суть события в 1-2 предложениях..." data-id="element-1833" />
                </div>

                <div data-id="element-1834">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1835">
                    Полное описание
                  </label>
                  <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[150px]" value={editingEvent.description_full} onChange={e => setEditingEvent({
                  ...editingEvent,
                  description_full: e.target.value
                })} placeholder="Подробное описание астрологического события..." data-id="element-1836" />
                </div>

                <div data-id="element-1837">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1838">
                    Влияние (на кого влияет)
                  </label>
                  <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingEvent.influence} onChange={e => setEditingEvent({
                  ...editingEvent,
                  influence: e.target.value
                })} data-id="element-1839" />
                </div>

                <div data-id="element-1840">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1841">
                    Рекомендации
                  </label>
                  <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingEvent.recommendations} onChange={e => setEditingEvent({
                  ...editingEvent,
                  recommendations: e.target.value
                })} data-id="element-1842" />
                </div>
              </div>
            </Card>
          </div>

          <div className="space-y-6" data-id="element-1843">
            <Card padding="lg" className="space-y-6" data-id="element-1844">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-1845">
                Публикация
              </h3>

              <div className="space-y-4" data-id="element-1846">
                <div data-id="element-1847">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1848">
                    Статус
                  </label>
                  <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingEvent.status} onChange={e => setEditingEvent({
                  ...editingEvent,
                  status: e.target.value as CmsStatus
                })} data-id="element-1849">
                    {Object.entries(STATUS_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-1850">
                        {val.ru}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-1851">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1852">
                    Дата авто-публикации
                  </label>
                  <Input type="datetime-local" value={editingEvent.publish_at ? editingEvent.publish_at.slice(0, 16) : ''} onChange={e => setEditingEvent({
                  ...editingEvent,
                  publish_at: e.target.value ? new Date(e.target.value).toISOString() : null
                })} data-id="element-1853" />
                  <p className="text-[10px] text-slate-500 mt-1" data-id="element-1854">
                    Оставьте пустым для ручной публикации
                  </p>
                </div>

                <div data-id="element-1855">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1856">
                    Дата авто-архивации
                  </label>
                  <Input type="datetime-local" value={editingEvent.hide_at ? editingEvent.hide_at.slice(0, 16) : ''} onChange={e => setEditingEvent({
                  ...editingEvent,
                  hide_at: e.target.value ? new Date(e.target.value).toISOString() : null
                })} data-id="element-1857" />
                </div>
              </div>
            </Card>

            <Card padding="lg" className="space-y-6" data-id="element-1858">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-1859">
                Мета-данные
              </h3>

              <div className="space-y-4" data-id="element-1860">
                <div data-id="element-1861">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1862">
                    Slug (URL)
                  </label>
                  <Input value={editingEvent.slug} onChange={e => setEditingEvent({
                  ...editingEvent,
                  slug: e.target.value
                })} placeholder="my-event-slug" data-id="element-1863" />
                </div>

                <div data-id="element-1864">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1865">
                    Важность
                  </label>
                  <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingEvent.importance} onChange={e => setEditingEvent({
                  ...editingEvent,
                  importance: e.target.value as any
                })} data-id="element-1866">
                    <option value="low" data-id="element-1867">Низкая</option>
                    <option value="medium" data-id="element-1868">Средняя</option>
                    <option value="high" data-id="element-1869">Высокая</option>
                    <option value="critical" data-id="element-1870">Критическая</option>
                  </select>
                </div>

                <div data-id="element-1871">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1872">
                    Теги (через запятую)
                  </label>
                  <Input value={editingEvent.tags.join(', ')} onChange={e => setEditingEvent({
                  ...editingEvent,
                  tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                })} placeholder="затмение, овен, карма" data-id="element-1873" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1874">
      {renderTableHeader('Астрологические события', filteredEvents.length, () => success('Экспорт событий начат'), handleCreateNew, 'Создать событие')}

      <div className="flex flex-col sm:flex-row gap-4 justify-between" data-id="element-1875">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} variant="pills" data-id="element-1876" />

        <div className="flex items-center gap-3" data-id="element-1877">
          <div className="relative w-full sm:w-64" data-id="element-1878">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1879" />
            <input type="text" placeholder="Поиск событий..." className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1880" />
          </div>

          <select className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={filterType} onChange={e => setFilterType(e.target.value as any)} data-id="element-1881">
            <option value="all" data-id="element-1882">Все типы</option>
            {Object.entries(EVENT_TYPE_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-1883">
                {val.ru}
              </option>)}
          </select>
        </div>
      </div>

      <Card padding="none" className="overflow-hidden" data-id="element-1884">
        <div className="overflow-x-auto" data-id="element-1885">
          <table className="w-full text-left border-collapse" data-id="element-1886">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-1887">
              <tr data-id="element-1888">
                <th className="px-4 py-3 font-medium" data-id="element-1889">Событие</th>
                <th className="px-4 py-3 font-medium" data-id="element-1890">Дата</th>
                <th className="px-4 py-3 font-medium" data-id="element-1891">Тип</th>
                <th className="px-4 py-3 font-medium" data-id="element-1892">Язык</th>
                <th className="px-4 py-3 font-medium" data-id="element-1893">Статус</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-1894">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1895">
              {filteredEvents.length > 0 ? filteredEvents.map(event => {
              const typeInfo = EVENT_TYPE_LABELS[event.event_type];
              const statusInfo = STATUS_LABELS[event.status];
              return <tr key={event.id} className="hover:bg-slate-50 transition-colors group" data-id="element-1896">
                      <td className="px-4 py-3" data-id="element-1897">
                        <div className="font-medium text-slate-900 text-sm" data-id="element-1898">
                          {event.title}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate max-w-xs" data-id="element-1899">
                          {event.description_short}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1900">
                        <div className="flex items-center gap-1.5 text-sm text-slate-700" data-id="element-1901">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" data-id="element-1902" />
                          {formatDateRu(event.date_start)}
                        </div>
                        {event.time_exact && <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5" data-id="element-1903">
                            <Clock className="w-3 h-3" data-id="element-1904" />
                            {event.time_exact}
                          </div>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1905">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeInfo.bg} ${typeInfo.color}`} data-id="element-1906">
                          {typeInfo.ru}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-1907">
                        <div className="flex items-center gap-1.5 text-xs text-slate-600 uppercase font-medium" data-id="element-1908">
                          <Globe className="w-3 h-3 text-slate-400" data-id="element-1909" />
                          {event.lang}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1910">
                        <Badge variant={statusInfo.variant as any} size="sm" data-id="element-1911">
                          {statusInfo.ru}
                        </Badge>
                        {event.publish_at && event.status === 'scheduled' && <div className="text-[10px] text-slate-500 mt-1" data-id="element-1912">
                            {formatDateRu(event.publish_at)}
                          </div>}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-1913">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1914">
                          {event.status === 'draft' && <Button variant="ghost" size="sm" onClick={() => handleStatusChange(event.id, 'published')} title="Опубликовать" data-id="element-1915">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-1916" />
                            </Button>}
                          {event.status === 'published' && <Button variant="ghost" size="sm" onClick={() => handleStatusChange(event.id, 'hidden')} title="Скрыть" data-id="element-1917">
                              <XCircle className="w-4 h-4 text-slate-400" data-id="element-1918" />
                            </Button>}
                          <Button variant="ghost" size="sm" onClick={() => setEditingEvent(event)} title="Редактировать" data-id="element-1919">
                            <Edit2 className="w-4 h-4 text-blue-500" data-id="element-1920" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)} title="Удалить" data-id="element-1921">
                            <Trash2 className="w-4 h-4 text-rose-500" data-id="element-1922" />
                          </Button>
                        </div>
                      </td>
                    </tr>;
            }) : <tr data-id="element-1923">
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-id="element-1924">
                    <div className="flex flex-col items-center justify-center" data-id="element-1925">
                      <AlertCircle className="w-8 h-8 text-slate-300 mb-2" data-id="element-1926" />
                      <p data-id="element-1927">События не найдены</p>
                      <Button variant="outline" size="sm" className="mt-4" onClick={handleCreateNew} data-id="element-1928">
                        Создать первое событие
                      </Button>
                    </div>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}